<template>
	<div class="pdf-flip-book" v-loading="loading" element-loading-text="PDF 加载中...">
		<div class="book-wrapper" ref="bookWrapper">
			<div class="flip-book" ref="flipBook"></div>
		</div>

		<div class="pdf-toolbar" v-if="totalPages > 0">
			<el-button size="small" icon="el-icon-arrow-left" circle @click="flipPrev"></el-button>
			<span class="page-indicator">
				<el-input-number v-model="currentPage" :min="1" :max="totalPages" size="mini"
					:controls="false" class="page-input" @change="goToPage"></el-input-number>
				/ {{ totalPages }}
			</span>
			<el-button size="small" icon="el-icon-arrow-right" circle @click="flipNext"></el-button>
			<el-button size="small" icon="el-icon-full-screen" circle title="全屏" @click="toggleFullscreen"></el-button>
		</div>
	</div>
</template>

<script>
// PDF 翻书组件：PDF.js 逐页渲染 + StPageFlip 翻页动画
// 用法：<pdf-flip-book pdf-url="/xxx.pdf" />
import { PageFlip } from 'page-flip'
// webpack4/老 babel 无法解析主构建的私有类字段语法，必须用 legacy(ES5) 构建
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'

export default {
	name: 'PdfFlipBook',
	props: {
		// 支持 本地 /public 下的路径或任意可跨域访问的 http(s) 地址
		pdfUrl: { type: String, required: true }
	},
	data() {
		return {
			loading: true,
			totalPages: 0,
			currentPage: 1,
			rendered: {},      // 已渲染页
			pdfDoc: null,
			pageFlip: null,
			pageRatio: 1.414,  // 页高/页宽，默认 A4
			destroyed: false,
			isFullscreen: false
		}
	},
	mounted() {
		pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'
		this.init()
		this._onFsChange = () => {
			this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement)
		}
		document.addEventListener('fullscreenchange', this._onFsChange)
	},
	beforeDestroy() {
		this.destroyed = true
		document.removeEventListener('fullscreenchange', this._onFsChange)
		if (this.pageFlip) {
			try { this.pageFlip.destroy() } catch (e) { /* ignore */ }
			this.pageFlip = null
		}
		if (this.pdfDoc) {
			try { this.pdfDoc.destroy() } catch (e) { /* ignore */ }
			this.pdfDoc = null
		}
	},
	methods: {
		async init() {
			try {
				const task = pdfjsLib.getDocument(this.pdfUrl)
				this.pdfDoc = await task.promise
				if (this.destroyed) return
				this.totalPages = this.pdfDoc.numPages
				// 取第一页宽高比，作为书本尺寸基准
				const p1 = await this.pdfDoc.getPage(1)
				const vp = p1.getViewport({ scale: 1 })
				this.pageRatio = vp.height / vp.width
				this.$nextTick(() => this.buildBook())
			} catch (e) {
				console.error(e)
				this.$message.error('PDF 加载失败：' + (e && e.message ? e.message : '未知错误'))
				this.loading = false
			}
		},
		buildBook() {
			const el = this.$refs.flipBook
			if (!el) return
			el.innerHTML = ''
			// StPageFlip HTML 模式：每个直接子元素为一页
			const nodes = document.createDocumentFragment()
			for (let i = 1; i <= this.totalPages; i++) {
				const div = document.createElement('div')
				div.className = 'flip-page'
				div.dataset.page = i
				const placeholder = document.createElement('div')
				placeholder.className = 'page-loading'
				placeholder.innerText = i
				div.appendChild(placeholder)
				nodes.appendChild(div)
			}
			el.appendChild(nodes)
			const baseW = 550
			this.pageFlip = new PageFlip(el, {
				width: baseW,
				height: Math.round(baseW * this.pageRatio),
				size: 'stretch',
				minWidth: 260,
				maxWidth: 1100,
				minHeight: 320,
				maxHeight: 1556,
				maxShadowOpacity: 0.5,
				showCover: true,
				mobileScrollSupport: false,
				flippingTime: 600,
				usePortrait: true
			})
			this.pageFlip.loadFromHTML(el.querySelectorAll('.flip-page'))
			const sync = (data) => {
				const idx = typeof data === 'number'
					? data
					: (data && typeof data.page === 'number' ? data.page : null)
				if (idx == null || idx < 0) return
				this.currentPage = idx + 1
				this.loadVisiblePages()
				this.$emit('page-change', this.currentPage)
			}
			this.pageFlip.on('flip', (e) => sync(e.data))
			this.pageFlip.on('change', (e) => sync(e.data))
			this.loading = false
			this.loadVisiblePages()
		},
		// 懒渲染：当前页 ±2 页
		loadVisiblePages() {
			const from = Math.max(1, this.currentPage - 2)
			const to = Math.min(this.totalPages, this.currentPage + 2)
			for (let i = from; i <= to; i++) this.renderPage(i)
		},
		async renderPage(n) {
			if (n < 1 || n > this.totalPages) return
			if (this.rendered[n] || this.pdfDoc == null) return
			this.rendered[n] = 'pending'
			try {
				const page = await this.pdfDoc.getPage(n)
				if (this.destroyed) return
				const el = this.$refs.flipBook
				if (!el) return
				const baseVp = page.getViewport({ scale: 1 })
				const targetW = 700 // 渲染精度（略高于显示尺寸，保证清晰）
				const vp = page.getViewport({ scale: targetW / baseVp.width })
				const canvas = document.createElement('canvas')
				canvas.width = Math.round(vp.width)
				canvas.height = Math.round(vp.height)
				await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
				if (this.destroyed) return
				const holder = el.querySelector(`[data-page="${n}"]`)
				if (!holder) return
				holder.innerHTML = ''
				canvas.className = 'page-canvas'
				holder.appendChild(canvas)
				this.rendered[n] = true
			} catch (e) {
				console.error('渲染第 ' + n + ' 页失败', e)
				this.rendered[n] = false
			}
		},
		flipPrev() { if (this.pageFlip) this.pageFlip.flipPrev() },
		flipNext() { if (this.pageFlip) this.pageFlip.flipNext() },
		goToPage(v) {
			if (!this.pageFlip || v == null) return
			const target = v - 1
			const cur = this.pageFlip.getCurrentPageIndex()
			if (Math.abs(target - cur) > 2) {
				this.pageFlip.turnToPage(target)
				this.currentPage = v
				this.loadVisiblePages()
			} else {
				this.pageFlip.flip(target)
			}
		},
		toggleFullscreen() {
			const node = this.isFullscreen ? document : this.$refs.bookWrapper
			if (this.isFullscreen) {
				(document.exitFullscreen || document.webkitExitFullscreen).call(document)
			} else if (node) {
				(node.requestFullscreen || node.webkitRequestFullscreen).call(node)
			}
		}
	}
}
</script>

<style>
/* 不能用 scoped：页内 DOM 由 JS 动态创建，拿不到 scoped 属性 */
.pdf-flip-book .book-wrapper {
	display: flex;
	justify-content: center;
	padding: 28px 12px;
	background: linear-gradient(180deg, #f2f3f7, #e6e8ee);
	border-radius: 8px;
	min-height: 420px;
}
.pdf-flip-book .flip-book {
	box-shadow: 0 14px 40px rgba(0, 0, 0, 0.28);
	border-radius: 4px;
}
.pdf-flip-book .flip-page {
	background: #fff;
	overflow: hidden;
}
.pdf-flip-book .page-loading {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	font-size: 46px;
	color: #d9dbe0;
	font-family: Georgia, serif;
}
.pdf-flip-book .page-canvas {
	display: block;
	width: 100%;
	height: 100%;
}
.pdf-flip-book .pdf-toolbar {
	margin-top: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
}
.pdf-flip-book .page-indicator {
	margin: 0 14px;
	color: #555;
	font-size: 14px;
}
.pdf-flip-book .page-input {
	width: 60px;
	margin-right: 6px;
}
.pdf-flip-book .page-input .el-input__inner {
	text-align: center;
}
/* 全屏时背景铺黑，书本居中 */
.pdf-flip-book .book-wrapper:fullscreen {
	background: #14161a;
}
</style>
